import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Award, ArrowLeft, Check, ShieldCheck, Ticket, DollarSign, Calendar, Printer, FileDown, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import SEO from "../components/SEO";
import { RaffleOnlinePayments } from "../components/RaffleOnlinePayments";
import { RaffleOfflineInstructions } from "../components/RaffleOfflineInstructions";
import { isOnlineRafflePayment, isOfflineRafflePayment } from "../lib/rafflePaymentChoice";
import { RafflePhotoGallery } from "../components/RafflePhotoGallery";
import { RafflePrizeDisclosure } from "../components/RafflePrizeDisclosure";
import { formatApiError } from "../lib/errors";
import { SITE_URL } from "../lib/publicSite";

const PAYMENT_OPTIONS = [
    { value: "card", label: "Credit or debit card (online)" },
    { value: "cash", label: "Cash (meetup or mail)" },
    { value: "check", label: "Check (meetup or mail)" },
];

const RaffleDetail = () => {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const paramEntryId = searchParams.get("entry") || "";
    const paramEmail = searchParams.get("email") || "";
    const isPreview = searchParams.get("preview") === "1";
    const [raffle, setRaffle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [form, setForm] = useState({
        entrant_name: "",
        entrant_email: "",
        entrant_phone: "",
        entrant_address: "",
        ticket_count: 1,
        payment_method: "cash",
        payment_delivery: "drop_off",
        rules_ack: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [paymentReview, setPaymentReview] = useState("");

    useEffect(() => {
        // Server checks the existing session and draft ownership for private previews.
        let cancelled = false;
        setLoading(true); setRaffle(null); setLoadError("");
        const endpoint = isPreview ? `/admin/raffles/preview/${slug}` : `/raffles/${slug}`;
        api.get(endpoint)
            .then((r) => { if (!cancelled) setRaffle(r.data); })
            .catch((e) => { if (!cancelled) setLoadError(e.response?.status === 401 ? "Sign in to preview this raffle." : e.response?.status === 403 ? "This raffle preview is not available to your account." : "Raffle not found."); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [slug, isPreview]);

    // Deep-link hydration: if the URL includes ?entry={id}&email={email}
    // (from a raffle confirmation email or a bookmarked ticket page),
    // fetch the entry immediately so the buyer lands on the celebratory
    // paid ticket view without re-submitting the form.
    useEffect(() => {
        if (!paramEntryId || !paramEmail) return;
        (async () => {
            try {
                const { data } = await api.get(`/raffles/entries/${paramEntryId}`, { params: { email: paramEmail } });
                if (data.payment_review) setPaymentReview(data.payment_review_reason || "Please contact the foundation about this payment.");
                setSuccess({
                    entry_id: paramEntryId,
                    amount_due_usd: data.amount_due_usd,
                    payment_method: data.payment_method,
                    payment_delivery: data.payment_delivery,
                    cancelled: Boolean(data.cancelled_at),
                    message: data.cancelled_at ? "This unpaid reservation was cancelled. No tickets were issued and no payment is due." : data.paid
                        ? "Payment confirmed — your ticket numbers are ready."
                        : data.message || "Your entry is still awaiting payment. Ticket numbers will appear here once we confirm the funds.",
                });
                if (data.paid && (data.ticket_numbers || []).length > 0) {
                    setPaidEntry(data);
                } else if (!data.cancelled_at) {
                    // Not yet paid — poll a few rounds in case the admin flips it while the buyer sits here.
                    pollForPaidEntry(paramEntryId, paramEmail);
                }
            } catch {
                // silently ignore — bad token / wrong email just falls back to the entry form
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramEntryId, paramEmail]);

    const submit = async (e) => {
        e.preventDefault();
        const tc = parseInt(form.ticket_count, 10);
        if (!tc || tc < 1) { toast.error("Pick at least 1 ticket"); return; }
        if (tc > raffle.max_tickets_per_person) {
            toast.error(`Maximum ${raffle.max_tickets_per_person} tickets per person.`); return;
        }
        if (!form.rules_ack) {
            toast.error("Please confirm you have read the raffle rules before entering.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post(`/raffles/${slug}/enter`, { ...form, ticket_count: tc });
            setSuccess(data);
            toast.success("Entry submitted!");
            // Kick off the paid-entry poll for ALL entries — cash/check take
            // longer (admin has to mark paid), but if the admin flips it
            // while the buyer is still on the page, the success card should
            // live-upgrade to the celebratory ticket view without a reload.
            if (data.entry_id && form.entrant_email) {
                pollForPaidEntry(data.entry_id, form.entrant_email);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Could not submit entry"));
        } finally {
            setSubmitting(false);
        }
    };

    // After an online payment (Stripe/PayPal) captures, the backend flips
    // the entry to paid + issues ticket numbers. Poll our public
    // entry-lookup endpoint for up to ~15s so the success screen can render
    // the real ticket numbers + prize photo + "Print" / "Download PDF"
    // buttons without a page reload.
    const [paidEntry, setPaidEntry] = useState(null);
    const pollForPaidEntry = async (entryId, email) => {
        if (!entryId || !email) return null;
        for (let i = 0; i < 10; i++) {
            try {
                const { data } = await api.get(`/raffles/entries/${entryId}`, { params: { email } });
                if (data.payment_review) { setPaymentReview(data.payment_review_reason || "Please contact the foundation about this payment."); return data; }
                if (data.paid && (data.ticket_numbers || []).length > 0) {
                    setPaidEntry(data);
                    if (!["pending", "sending", "unconfigured"].includes(data.ticket_email_status)) return data;
                }
                if (data.cancelled_at) { setSuccess(prev => ({ ...prev, cancelled: true, message: "This unpaid reservation was cancelled. No payment is due." })); return data; }
            } catch {
                /* ignore transient */
            }
            await new Promise((r) => setTimeout(r, 1500));
        }
        return null;
    };

    const printTickets = () => {
        // Uses a hidden print-only iframe so we render just the tickets,
        // never the surrounding page chrome. Falls back to window.print
        // scoped by @media print CSS on `.ticket-printable`.
        window.print();
    };

    const downloadTicketPdf = async () => {
        if (!paidEntry) return;
        try {
            const url = `${API}/raffles/entries/${paidEntry.id}/ticket.pdf?email=${encodeURIComponent(paidEntry.entrant_email)}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error("download failed");
            const blob = await res.blob();
            const objUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objUrl;
            a.download = `raffle-ticket-${paidEntry.raffle.slug}-${paidEntry.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(objUrl);
        } catch {
            toast.error("Could not download PDF");
        }
    };

    if (loading) {
        return <div data-testid="raffle-detail-loading" className="min-h-screen bg-[#FDFBF7] py-20 text-center text-sm text-[#4A5568]">Loading…</div>;
    }
    if (!raffle) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] py-20 text-center">
                <SEO title="Raffle not available" path={`/raffles/${encodeURIComponent(slug)}`} noIndex />
                <p role="alert" data-testid="raffle-detail-error" className="font-heading text-2xl text-[#1C252E]">{loadError || "Raffle not found."}</p>
                <Link data-testid="raffle-error-back-link" to="/raffles" className="text-[#E53935] text-sm mt-3 inline-block hover:underline">← Back to raffles</Link>
            </div>
        );
    }

    const total = (parseFloat(raffle.ticket_price_usd) * (parseInt(form.ticket_count, 10) || 0)).toFixed(2);
    const isOpen = raffle.status === "open" && !raffle.drawn_at && (!raffle.starts_at || new Date(raffle.starts_at).getTime() <= Date.now()) && (!raffle.ends_at || new Date(raffle.ends_at).getTime() >= Date.now());

    return (
        <div className="min-h-screen bg-[#FDFBF7] [overflow-wrap:anywhere]" data-testid="raffle-detail">
            <SEO title={isPreview ? "Private raffle preview" : raffle.name} description={isPreview ? "Private raffle preview." : `${raffle.prize_description || raffle.name}. Review eligibility, ticket details and official rules. Supports Reddog Community Foundation in Frenchtown, Montana.`.slice(0, 180)} path={`/raffles/${encodeURIComponent(slug)}`} image={isPreview ? undefined : raffle.prize_image_url} noIndex={isPreview || raffle.status === "draft" || Boolean(paramEntryId)} />
            <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
                <Link data-testid="raffle-detail-back-link" to="/raffles" className="inline-flex items-center gap-1 text-sm text-[#4A5568] hover:text-[#E53935] mb-6">
                    <ArrowLeft className="w-4 h-4" strokeWidth={2} /> All raffles
                </Link>

                {isPreview && (
                    <div
                        data-testid="raffle-preview-banner"
                        className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 mb-6 flex items-start gap-3"
                        role="note"
                    >
                        <ShieldCheck className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" strokeWidth={2} />
                        <div className="text-sm text-amber-900 leading-relaxed">
                            <p className="font-bold uppercase tracking-wider text-xs mb-1">Private raffle preview</p>
                            <p>
                                <strong data-testid="raffle-preview-status">Status: {raffle.status}</strong>.
                                {raffle.status === "draft" ? " Draft — not published." : " Published raffle."}
                            </p>
                        </div>
                    </div>
                )}

                {/* Prominent Montana state-law banner — required notice */}
                <aside data-testid="raffle-state-law-banner" role="note" aria-label="State law payment notice" className="bg-[#1C252E] text-white rounded-2xl p-5 mb-6 flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-[#E53935] shrink-0 mt-0.5" strokeWidth={2} />
                    <div className="text-sm leading-relaxed">
                        <p className="font-bold text-[#E53935] text-xs uppercase tracking-wider mb-1">Montana State Law — Raffle Payment Notice</p>
                        <p>Under Montana HB141 (2023), nonprofit raffles may accept <strong>credit + debit cards, cash, and checks</strong>. 100% of proceeds fund charitable purposes; individual prize value capped at $5,000; drawing date is fixed before ticket sales open. Records retained 12 months post-draw per MCA 23-5-413.</p>
                    </div>
                </aside>

                <header className="mb-8">
                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${isOpen ? "bg-green-100 text-green-800" : "bg-[#F4F1EA] text-[#4A5568]"}`}>
                        {isOpen ? "Open for entries" : (raffle.drawn_at ? "Drawn" : "Closed")}
                    </span>
                    <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight leading-[1.05] mt-3">{raffle.name}</h1>
                    {raffle.description && <p className="text-base text-[#4A5568] mt-4 max-w-2xl whitespace-pre-line">{raffle.description}</p>}
                </header>

                {raffle.winner_entry_public && (
                    <div className="mb-8 bg-amber-50 border border-amber-200 rounded-2xl p-5" data-testid="raffle-winner-banner">
                        <p className="text-xs uppercase tracking-wider font-bold text-amber-900 mb-1">🎉 Winner announced</p>
                        <p className="text-[#1C252E]">
                            <strong>{raffle.winner_entry_public.first_name} {raffle.winner_entry_public.last_initial}</strong> — winning ticket #{raffle.winner_entry_public.winning_ticket_number}
                        </p>
                        <p className="text-xs text-[#4A5568] mt-1">Drawn {new Date(raffle.drawn_at).toLocaleString()}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <aside className="lg:col-span-1 space-y-4 min-w-0">
                        <RafflePhotoGallery key={raffle.id} raffle={raffle} />
                        {!isPreview && raffle.status !== "draft" && <RaffleShareBlock raffle={raffle} />}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="raffle-detail-funds-block">
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-800 mb-2">Where the money goes</p>
                            <p className="text-sm text-emerald-900">
                                {raffle.campaign_name
                                    ? <>100% of proceeds fund <strong>{raffle.campaign_name}</strong>.</>
                                    : <>100% of proceeds go to Reddog Foundation&apos;s <strong>General Support Fund</strong>, which pays for approved programs.</>}
                            </p>
                            <p className="text-xs text-emerald-800 mt-2">
                                Each paid ticket is booked as a donation in our public ledger and shows up on the transparency page.
                            </p>
                        </div>
                        <div className="bg-white border border-black/10 rounded-2xl p-5">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#E53935] mb-2 flex items-center gap-1"><Award className="w-3.5 h-3.5" /> The prize</p>
                            <p className="text-[#1C252E] font-medium">{raffle.prize_description}</p>
                            {raffle.prize_value_usd && <p className="text-sm text-[#4A5568] mt-1">Value: ${raffle.prize_value_usd}</p>}
                            {raffle.prize_source && <p className="text-xs text-[#4A5568] mt-2 italic">{raffle.prize_source}</p>}
                        </div>
                        <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-2 text-sm">
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Ticket price</span><strong className="text-[#1C252E]">${raffle.ticket_price_usd}</strong></p>
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Tickets sold</span><strong className="text-[#1C252E]">{raffle.total_tickets_sold || 0}</strong></p>
                            <p className="flex items-center justify-between"><span className="text-[#4A5568]">Max per person</span><strong className="text-[#1C252E]">{raffle.max_tickets_per_person}</strong></p>
                            {raffle.ends_at && (
                                <p className="flex items-center gap-1.5 text-xs text-[#4A5568] pt-2 border-t border-black/5"><Calendar className="w-3.5 h-3.5" /> Drawing: {new Date(raffle.ends_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</p>
                            )}
                        </div>
                        <div className="bg-[#F4F1EA] rounded-2xl p-5 text-xs text-[#4A5568] space-y-2">
                            <p className="flex items-center gap-1.5 text-[#1C252E] font-bold uppercase tracking-wider"><ShieldCheck className="w-3.5 h-3.5 text-[#E53935]" /> Montana compliance</p>
                            <p>• 100% of proceeds support Reddog Community Foundation.</p>
                            <p>• <strong>Payment accepted:</strong> credit/debit card, cash, check — all allowed under HB141 (2023).</p>
                            <p>• Prize cap $5,000 per ticket. Foundation owns the prize before ticket sales open.</p>
                            <p>• Random draw via cryptographically-secure RNG, recorded for audit.</p>
                            <p>• Need not be present to win — we&apos;ll contact the winner using the info provided.</p>
                            <p>• Records retained 12 months after the draw per state law.</p>
                        </div>
                        <div
                            data-testid="raffle-contact-block"
                            className="bg-white border border-black/10 rounded-2xl p-5 text-xs text-[#4A5568] space-y-2"
                        >
                            <p className="text-[#1C252E] font-bold uppercase tracking-wider text-[10px]">Questions or mailing a check?</p>
                            <address className="not-italic text-[#1C252E] leading-relaxed">
                                <div className="font-semibold">Reddog Community Foundation Inc.</div>
                                <div>PO Box 231</div>
                                <div>Frenchtown, MT 59834</div>
                            </address>
                            <a
                                href="tel:+14068020279"
                                data-testid="raffle-contact-phone"
                                className="inline-block font-semibold text-[#1C252E] hover:text-[#E53935] transition-colors"
                            >
                                (406) 802-0279
                            </a>
                            <p className="text-[10px] text-[#4A5568]">Make checks payable to <strong>Reddog Community Foundation Inc.</strong></p>
                        </div>
                    </aside>

                    <main className="lg:col-span-2 min-w-0">
                        {success ? (
                            <RaffleSuccessCard
                                success={success}
                                paidEntry={paidEntry}
                                raffle={raffle}
                                onPrint={printTickets}
                                onDownloadPdf={downloadTicketPdf}
                            />
                        ) : !isOpen ? (
                            <div className="bg-white border border-black/10 rounded-2xl p-8 text-center" data-testid="raffle-closed-msg">
                                <p className="font-heading text-xl text-[#1C252E]">This raffle is {raffle.drawn_at ? "complete" : "not currently accepting entries"}.</p>
                                <Link to="/raffles" className="inline-block mt-4 text-sm text-[#E53935] hover:underline">See other raffles →</Link>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 space-y-4" data-testid="raffle-entry-form">
                                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Enter the raffle</h2>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label htmlFor="rf-name" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Full name *</label>
                                        <input id="rf-name" data-testid="raffle-name-input" required value={form.entrant_name} onChange={(e) => setForm({ ...form, entrant_name: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                    <div>
                                        <label htmlFor="rf-email" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Email *</label>
                                        <input id="rf-email" type="email" data-testid="raffle-email-input" required value={form.entrant_email} onChange={(e) => setForm({ ...form, entrant_email: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                    <div>
                                        <label htmlFor="rf-phone" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Phone *</label>
                                        <input id="rf-phone" data-testid="raffle-phone-input" required value={form.entrant_phone} onChange={(e) => setForm({ ...form, entrant_phone: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Required to contact you if you win" />
                                    </div>
                                    <div>
                                        <label htmlFor="rf-tickets" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Tickets ({raffle.max_tickets_per_person} max)</label>
                                        <input id="rf-tickets" type="number" min="1" max={raffle.max_tickets_per_person} data-testid="raffle-tickets-input" required value={form.ticket_count} onChange={(e) => setForm({ ...form, ticket_count: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="rf-addr" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Mailing address *</label>
                                    <textarea id="rf-addr" rows={2} data-testid="raffle-address-input" required value={form.entrant_address} onChange={(e) => setForm({ ...form, entrant_address: e.target.value })} className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]" placeholder="Required for Montana record-keeping" />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Payment method *</label>
                                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        {PAYMENT_OPTIONS.map((opt) => (
                                            <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${form.payment_method === opt.value ? "bg-[#F2D8D5]/50 border-[#E53935]" : "bg-white border-black/10 hover:border-[#E53935]/40"}`}>
                                                <input type="radio" name="payment_method" value={opt.value} checked={form.payment_method === opt.value} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid={`raffle-pay-${opt.value}`} className="accent-[#E53935]" />
                                                <span className="text-sm text-[#1C252E]">{opt.label}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <p className="mt-2 text-xs text-[#4A5568] flex items-start gap-1.5"><ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-[#E53935]" /> Credit + debit cards, cash and checks are all accepted under Montana HB141 (2023).</p>
                                </div>
                                {form.payment_method !== "card" && <label className="block text-xs font-medium text-[#4A5568]">Payment delivery<select data-testid="raffle-payment-delivery" value={form.payment_delivery} onChange={e => setForm({ ...form, payment_delivery: e.target.value })} className="block w-full mt-1 rounded-lg border border-black/15 bg-white px-3 py-2 text-sm"><option value="drop_off">Meetup — we&apos;ll contact you</option><option value="pickup">Other arranged handoff</option><option value="mail">Mail {form.payment_method === "check" ? "a check" : "cash"}</option></select></label>}
                                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4" data-testid="raffle-rules-agreement">
                                    <input
                                        id="rf-rules-ack"
                                        type="checkbox"
                                        checked={form.rules_ack}
                                        onChange={(e) => setForm({ ...form, rules_ack: e.target.checked })}
                                        data-testid="raffle-rules-ack"
                                        className="mt-1 w-4 h-4 accent-[#E53935] shrink-0"
                                        required
                                    />
                                    <div className="min-w-0 space-y-2">
                                        <label htmlFor="rf-rules-ack" data-testid="raffle-rules-ack-label" className="block cursor-pointer text-sm text-[#1C252E] leading-relaxed">
                                            <strong>I have read and agree to the raffle rules and prize disclosure.</strong>{" "}
                                            <span className="text-[#4A5568]">
                                                I understand the winner is drawn using cryptographically secure random selection, that I must claim my prize within
                                                <strong> {raffle.claim_deadline_days || 30} days</strong> of being contacted (by phone, text, and email), and that if I don&apos;t respond in time the foundation will redraw a new winner from the remaining paid tickets. Records are retained 12 months post-draw per MT law.
                                            </span>
                                        </label>
                                        <RafflePrizeDisclosure />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between pt-4 border-t border-black/5 flex-wrap gap-3">
                                    <p className="text-sm text-[#4A5568]">
                                        Total due: <strong className="text-[#E53935] text-xl ml-1">${total}</strong>
                                    </p>
                                    <button type="submit" disabled={submitting || !form.rules_ack} data-testid="raffle-submit-btn" className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-6 py-2.5 text-sm font-medium transition">
                                        <Ticket className="w-4 h-4" strokeWidth={2} />
                                        {submitting ? "Submitting…" : "Reserve my tickets"}
                                    </button>
                                </div>

                            </form>
                        )}
                        {paymentReview && <div data-testid="raffle-payment-review" role="alert" className="mt-4 bg-amber-50 border border-amber-300 rounded-xl p-4 text-sm text-amber-950"><strong>Payment needs review.</strong> {paymentReview} No new tickets have been issued. Please contact the foundation before making another payment.</div>}
                        {success?.entry_id && isOnlineRafflePayment(success.payment_method) && !success.cancelled && !paidEntry && !paymentReview && isOpen && <RaffleOnlinePayments raffle={raffle} entry={success} email={form.entrant_email || paramEmail} name={form.entrant_name} onSuccess={() => { toast.success("Payment received — checking your tickets…"); pollForPaidEntry(success.entry_id, form.entrant_email || paramEmail); }} />}
                        {(success || !isOpen) && <div className="mt-4" data-testid="raffle-prize-disclosure-readonly"><RafflePrizeDisclosure /></div>}
                    </main>
                </div>
            </div>
        </div>
    );
};

/**
 * RaffleShareBlock — plain "spread the word" panel with copy-link,
 * native share, and one-tap Facebook / X / SMS / email buttons.
 * No referral tracking or credits — just makes it easy to share the URL.
 */
const RaffleShareBlock = ({ raffle }) => {
    const url = `${SITE_URL}/api/og/raffles/${encodeURIComponent(raffle.slug)}`;
    const qrSrc = `${API}/raffles/${raffle.slug}/qr.png?size=512`;
    const msg = `Support Reddog Community Foundation in Frenchtown, Montana. View ${raffle.name}, prize details and official eligibility rules.`;
    const [previewOpen, setPreviewOpen] = React.useState(false);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link copied — paste it anywhere!");
        } catch {
            toast.error("Could not copy — long-press the URL bar to copy manually");
        }
    };

    const nativeShare = async () => {
        if (!navigator.share) return copyLink();
        try {
            await navigator.share({ title: raffle.name, text: msg, url });
        } catch { /* user cancelled */ }
    };

    const downloadQr = async () => {
        try {
            const res = await fetch(qrSrc);
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `raffle-qr-${raffle.slug}.png`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success("QR downloaded — drop it on a flyer!");
        } catch {
            toast.error("Download failed");
        }
    };

    const enc = encodeURIComponent;
    const facebook = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
    const twitter = `https://twitter.com/intent/tweet?text=${enc(msg + " " + url)}`;
    const sms = `sms:?&body=${enc(msg + " " + url)}`;
    const email = `mailto:?subject=${enc(raffle.name)}&body=${enc(msg + "\n\n" + url)}`;

    return (
        <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-4" data-testid="raffle-share-block">
            <div className="flex items-center gap-2">
                <Share2 className="w-4 h-4 text-[#E53935]" strokeWidth={2} />
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Spread the word</p>
            </div>

            {/* QR + description stacked cleanly — no side-by-side squeeze in narrow columns */}
            <div className="flex flex-col items-center text-center gap-3">
                <img
                    src={qrSrc}
                    alt="Raffle QR code"
                    data-testid="raffle-share-qr-img"
                    className="w-32 h-32 rounded-lg border border-black/10 bg-white p-1"
                />
                <p className="text-xs text-[#4A5568] leading-relaxed max-w-[220px]">
                    Scan or share this QR — every scan lands on the entry form directly.
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                    <button
                        onClick={downloadQr}
                        data-testid="raffle-share-qr-download"
                        className="text-[11px] bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1.5 font-medium"
                    >
                        Download QR
                    </button>
                    <button
                        onClick={() => setPreviewOpen(true)}
                        data-testid="raffle-share-preview-btn"
                        className="text-[11px] border border-black/15 hover:border-[#E53935] text-[#1C252E] rounded-full px-3 py-1.5 font-medium"
                    >
                        Preview flyer
                    </button>
                </div>
            </div>

            {/* Copy link input */}
            <div className="flex gap-2">
                <input
                    readOnly
                    value={url}
                    data-testid="raffle-share-url"
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 rounded-lg border border-black/10 bg-[#FDFBF7] px-3 py-2 text-xs font-mono text-[#4A5568] focus:outline-none focus:border-[#E53935] truncate"
                />
                <button
                    onClick={copyLink}
                    data-testid="raffle-share-copy-btn"
                    className="bg-[#1C252E] hover:bg-black text-white rounded-lg px-3 py-2 text-xs font-medium whitespace-nowrap"
                >
                    Copy
                </button>
            </div>

            {/* Social buttons — 2×2 grid on narrow columns to avoid text wrap */}
            <div className="grid grid-cols-2 gap-2">
                <a href={facebook} target="_blank" rel="noopener noreferrer" data-testid="raffle-share-facebook" className="flex items-center justify-center rounded-lg border border-black/10 hover:border-[#1877F2] hover:text-[#1877F2] py-2 text-xs font-medium text-[#4A5568]">Facebook</a>
                <a href={twitter} target="_blank" rel="noopener noreferrer" data-testid="raffle-share-twitter" className="flex items-center justify-center rounded-lg border border-black/10 hover:border-black hover:text-black py-2 text-xs font-medium text-[#4A5568]">X&nbsp;/&nbsp;Twitter</a>
                <a href={sms} data-testid="raffle-share-sms" className="flex items-center justify-center rounded-lg border border-black/10 hover:border-[#16A34A] hover:text-[#16A34A] py-2 text-xs font-medium text-[#4A5568]">Text</a>
                <a href={email} data-testid="raffle-share-email" className="flex items-center justify-center rounded-lg border border-black/10 hover:border-[#E53935] hover:text-[#E53935] py-2 text-xs font-medium text-[#4A5568]">Email</a>
            </div>

            {typeof navigator !== "undefined" && navigator.share && (
                <button
                    onClick={nativeShare}
                    data-testid="raffle-share-native-btn"
                    className="w-full text-xs text-[#E53935] hover:underline font-medium"
                >
                    More share options →
                </button>
            )}

            {/* Preview flyer modal */}
            {previewOpen && (
                <div
                    className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
                    onClick={() => setPreviewOpen(false)}
                    data-testid="raffle-flyer-preview"
                >
                    <div
                        className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Flyer preview — this is what a paper printout would look like */}
                        <div className="bg-[#FDFBF7] p-8 text-center border-b border-black/10">
                            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E53935] mb-2">Reddog Community Foundation</p>
                            <h2 className="font-heading text-3xl text-[#1C252E] font-medium mb-3">{raffle.name}</h2>
                            {raffle.prize_image_url && (
                                <img src={raffle.prize_image_url} alt={raffle.prize_description} className="w-full max-w-xs mx-auto rounded-xl border border-black/10 mb-4" />
                            )}
                            <p className="text-sm text-[#4A5568] mb-4 max-w-xs mx-auto">{raffle.prize_description}</p>
                            <p className="text-2xl font-bold text-[#E53935] mb-4">${raffle.ticket_price_usd?.toFixed(2)} per ticket</p>
                            <div className="inline-block bg-white p-3 rounded-lg border border-black/10 mb-3">
                                <img src={qrSrc} alt="QR" className="w-40 h-40" />
                            </div>
                            <p className="text-xs text-[#4A5568] font-medium">SCAN TO ENTER</p>
                            <p className="text-[10px] text-[#4A5568] mt-3 max-w-xs mx-auto leading-relaxed">100% of proceeds fund {raffle.campaign_name || "the General Support Fund"}. Winner drawn by certified RNG.</p>
                        </div>
                        <div className="p-4 flex justify-end gap-2 bg-white">
                            <button onClick={() => setPreviewOpen(false)} className="px-4 py-2 rounded-full border border-black/15 text-xs font-medium text-[#4A5568] hover:bg-[#F4F1EA]">Close</button>
                            <button onClick={() => window.print()} data-testid="raffle-flyer-print" className="px-4 py-2 rounded-full bg-[#E53935] hover:bg-[#D32F2F] text-white text-xs font-medium">Print flyer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * RaffleSuccessCard — the post-entry confirmation card.
 *   - While `paidEntry` is null (cash/check pending or online payment
 *     still processing) we show a friendly "we've got your info" state.
 *   - Once `paidEntry.paid` is true we render the celebratory version:
 *     giant prize photo, every ticket # as a big pill, Print + Download
 *     PDF buttons, and a `ticket-printable` DOM subtree that the print
 *     stylesheet keeps and hides everything else on the page.
 */
const RaffleSuccessCard = ({ success, paidEntry, raffle, onPrint, onDownloadPdf }) => {
    const paid = paidEntry?.paid && (paidEntry?.ticket_numbers || []).length > 0;
    const prizePhoto = paidEntry?.raffle?.prize_image_url || raffle?.prize_image_url || "";
    const prizeDesc = paidEntry?.raffle?.prize_description || raffle?.prize_description || "";
    const raffleName = paidEntry?.raffle?.name || raffle?.name || "the raffle";
    const drawDate = (paidEntry?.raffle?.ends_at || raffle?.ends_at || "").split("T")[0] || "TBD";

    return (
        <>
            {/* Print-only stylesheet — keeps just the ticket subtree visible */}
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    .ticket-printable, .ticket-printable * { visibility: visible !important; }
                    .ticket-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
                    .no-print { display: none !important; }
                }
            `}</style>
            <div className="bg-white border border-green-200 rounded-2xl p-8" data-testid="raffle-success">
                {paid ? (
                    <div className="ticket-printable">
                        <div className="text-center mb-6">
                            <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4 no-print">
                                <Check className="w-6 h-6 text-green-700" strokeWidth={2} />
                            </div>
                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">You&apos;re in — good luck!</p>
                            <h2 className="font-heading text-2xl text-[#1C252E] mt-1">{raffleName}</h2>
                            <p className="text-sm text-[#4A5568] mt-2" data-testid="raffle-success-entrant">{paidEntry.entrant_name} · {paidEntry.entrant_email}</p>
                        </div>

                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568] mb-2 text-center">Your ticket numbers</p>
                        <div className="flex flex-wrap justify-center gap-2 mb-6" data-testid="raffle-success-ticket-numbers">
                            {paidEntry.ticket_numbers.map((t) => (
                                <span
                                    key={t}
                                    className="inline-flex items-center justify-center bg-amber-50 text-amber-900 font-mono font-bold text-xl px-4 py-2 rounded-xl border border-amber-200 tracking-widest"
                                    data-testid={`raffle-ticket-${t}`}
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>

                        {prizePhoto && (
                            <img
                                src={prizePhoto}
                                alt="Prize"
                                className="w-full max-w-md mx-auto rounded-2xl border border-black/5 mb-4"
                                data-testid="raffle-success-prize-photo"
                            />
                        )}
                        <div className="bg-[#F4F1EA] rounded-xl p-4 mb-6 text-center">
                            <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-1">Prize</p>
                            <p className="text-sm text-[#1C252E]">{prizeDesc}</p>
                            <p className="text-xs text-[#4A5568] mt-2">Drawing on or after <strong>{drawDate}</strong> · Winner picked by certified RNG</p>
                        </div>

                        <div className="flex flex-wrap justify-center gap-3 no-print">
                            <button
                                onClick={onPrint}
                                data-testid="raffle-print-btn"
                                className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium"
                            >
                                <Printer className="w-4 h-4" strokeWidth={2} /> Print my tickets
                            </button>
                            <button
                                onClick={onDownloadPdf}
                                data-testid="raffle-pdf-btn"
                                className="inline-flex items-center gap-2 bg-white border border-black/15 hover:border-[#E53935] text-[#1C252E] rounded-full px-5 py-2.5 text-sm font-medium"
                            >
                                <FileDown className="w-4 h-4" strokeWidth={2} /> Download PDF
                            </button>
                        </div>

                        <p className="text-xs text-[#4A5568] mt-6 text-center no-print">
                            {paidEntry.ticket_email_status === "accepted_by_smtp" ? <>Your ticket email was accepted by the mail server for <strong>{paidEntry.entrant_email}</strong>.</> : ["pending", "sending", "unconfigured"].includes(paidEntry.ticket_email_status) ? "Your ticket email is queued." : "Email acceptance is not confirmed. You can print or download your tickets here."} Keep this page as your record.
                        </p>
                        <div className="text-center mt-4 no-print">
                            <Link to="/raffles" className="text-sm text-[#E53935] hover:underline">← Back to all raffles</Link>
                        </div>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-6 h-6 text-green-700" strokeWidth={2} />
                        </div>
                        <h2 data-testid="raffle-reservation-status" className="font-heading text-2xl text-[#1C252E]">{success.cancelled ? "Reservation cancelled" : "Entry received — payment pending"}</h2>
                        {!success.cancelled && isOfflineRafflePayment(success.payment_method) ? <RaffleOfflineInstructions /> : <p data-testid="raffle-reservation-message" className="text-sm text-[#4A5568] mt-3 max-w-md mx-auto">{success.message}</p>}
                        {!success.cancelled && <p data-testid="raffle-reservation-amount" className="text-base text-[#1C252E] font-medium mt-4">Amount due: <span className="text-[#E53935]">${success.amount_due_usd?.toFixed(2)}</span></p>}
                        <p data-testid="raffle-reservation-reference" className="text-xs text-[#4A5568] mt-1">Confirmation ID: <span className="font-mono">{success.entry_id}</span></p>
                        {!success.cancelled && <p data-testid="raffle-payment-pending-note" className="text-xs text-[#4A5568] mt-4 max-w-md mx-auto">Your entry remains pending until payment is received and confirmed. Your ticket numbers and printable ticket will then appear here, and your ticket email will be queued.</p>}
                        <Link data-testid="raffle-reservation-back-link" to="/raffles" className="inline-block mt-6 text-sm text-[#E53935] hover:underline">← Back to all raffles</Link>
                    </div>
                )}
            </div>
        </>
    );
};

export default RaffleDetail;
